import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAxios } from '@/lib/api';
import { logError } from '@/lib/logger';
import AdminHeader from '@/components/admin/AdminHeader';
import { AdminStats, AdminTabs } from '@/components/admin/AdminOverview';
import ProductsTab from '@/components/admin/ProductsTab';
import BlogTab from '@/components/admin/BlogTab';
import CertificatesTab from '@/components/admin/CertificatesTab';
import SitePhotosManager from '@/components/admin/SitePhotosManager';
import AnalyticsDashboard from '@/components/admin/AnalyticsDashboard';
import CountryInsights from '@/components/admin/analytics/CountryInsights';
import TestimonialsManager from '@/components/admin/TestimonialsManager';
import InquiryCRM from '@/components/admin/InquiryCRM';
import SEOManager from '@/components/admin/SEOManager';
import LandingPagesManager from '@/components/admin/LandingPagesManager';

export default function AdminPage() {
  const [tab, setTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsQuick, setAnalyticsQuick] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionStorage.getItem('admin_session')) { navigate('/login'); return; }
    authAxios.get('/api/auth/me')
      .catch(() => { sessionStorage.removeItem('admin_session'); navigate('/login'); });
  }, [navigate]);

  const handleLogout = useCallback(() => {
    authAxios.post('/api/auth/logout').catch(() => {});
    sessionStorage.removeItem('admin_session');
    navigate('/login');
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [inqRes, prodRes, certRes, blogRes] = await Promise.all([
        authAxios.get('/api/inquiries'),
        authAxios.get('/api/products'),
        authAxios.get('/api/certificates'),
        authAxios.get('/api/blog?published_only=false'),
      ]);
      setInquiries(inqRes.data);
      setProducts(prodRes.data);
      setCertificates(certRes.data);
      setBlogPosts(blogRes.data);
      authAxios.get('/api/analytics/dashboard?days=30').then(r => setAnalyticsQuick(r.data)).catch(() => {});
    } catch (error) {
      logError('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stats = useMemo(() => ({
    total: inquiries.length,
    new: inquiries.filter(i => (i.stage || i.status) === 'new').length,
    hot: inquiries.filter(i => i.priority === 'hot').length,
    countries: new Set(inquiries.map(i => i.country_group || i.country).filter(c => c && c !== 'Unknown')).size,
    products: products.length,
  }), [inquiries, products]);

  const tabCounts = useMemo(() => ({
    inquiries: stats.new, products: stats.products, blog: blogPosts.length, certificates: certificates.length,
  }), [stats, blogPosts, certificates]);

  return (
    <div className="min-h-screen pt-28 pb-16 bg-cream" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminHeader loading={loading} onRefresh={fetchAll} onLogout={handleLogout} />
        <AdminStats stats={stats} views={analyticsQuick?.total_period} />
        <AdminTabs active={tab} counts={tabCounts} onChange={setTab} />

        {tab === 'inquiries' && <InquiryCRM inquiries={inquiries} setInquiries={setInquiries} />}
        {tab === 'products' && <ProductsTab products={products} onRefresh={fetchAll} />}
        {tab === 'blog' && <BlogTab posts={blogPosts} onRefresh={fetchAll} />}
        {tab === 'certificates' && <CertificatesTab certificates={certificates} onRefresh={fetchAll} />}
        {tab === 'site-photos' && <SitePhotosManager />}
        {tab === 'testimonials' && <TestimonialsManager />}
        {tab === 'landing-pages' && <LandingPagesManager />}
        {tab === 'seo' && <SEOManager />}
        {tab === 'analytics' && (
          <div className="space-y-10">
            <CountryInsights />
            <AnalyticsDashboard />
          </div>
        )}
      </div>
    </div>
  );
}
